import * as t from 'io-ts';

import {
  makeEnumRuntimeType,
  RAccountIdRef,
  RCardGroupIdRef,
  RCardIdRef,
} from '../../../../../models/api/apiTypes';
import {
  RTableOffsetPaginationData,
  RTableTokenPaginationData,
} from '../../../../../models/table/tablePaginationDataModel';

/*
 * Runtime types.
 */

export enum ContactsManagerTableTypesEnum {
  ACCOUNTS = 'ACCOUNTS',
  ACCOUNT_CONTACTS = 'ACCOUNT_CONTACTS',
  CONTACTS = 'CONTACTS',
  LISTS = 'LISTS',
  SEARCH_CARD_GROUPS = 'SEARCH_CARD_GROUPS',
}

export enum ContactsManagerTableStatusesEnum {
  FETCH_REQUIRED = 'FETCH_REQUIRED',
  LOADING = 'LOADING',
  LOADED = 'LOADED',
}

export const RContactsManagerTableStatusesEnum = makeEnumRuntimeType(ContactsManagerTableStatusesEnum);

const RContactsManagerTableStateBase = t.readonly(
  t.type({
    type: makeEnumRuntimeType(ContactsManagerTableTypesEnum),
    totalResults: t.union([t.number, t.undefined]),
  }),
);

export const RContactsManagerAccountsTableStateDirty = t.intersection([
  RContactsManagerTableStateBase,
  t.type({
    type: t.literal(ContactsManagerTableTypesEnum.ACCOUNTS),
    status: RContactsManagerTableStatusesEnum,
    accountIds: t.readonlyArray(RAccountIdRef),
    selectedAccountIds: t.readonlyArray(RAccountIdRef),
  }),
  t.partial({
    paginationData: RTableTokenPaginationData,
  }),
]);

export const RContactsManagerAccountContactsTableStateDirty = t.intersection([
  RContactsManagerTableStateBase,
  t.type({
    type: t.literal(ContactsManagerTableTypesEnum.ACCOUNT_CONTACTS),
    accountId: RAccountIdRef,
    status: RContactsManagerTableStatusesEnum,
    cardIds: t.readonlyArray(RCardIdRef),
    selectedCardIds: t.readonlyArray(RCardIdRef),
    isInMultiSelectMode: t.boolean,
  }),
  t.partial({
    paginationData: RTableOffsetPaginationData,
  }),
]);

export const RContactsManagerContactsTableStateDirty = t.intersection([
  RContactsManagerTableStateBase,
  t.type({
    type: t.literal(ContactsManagerTableTypesEnum.CONTACTS),
    status: RContactsManagerTableStatusesEnum,
    cardIds: t.readonlyArray(RCardIdRef),
    selectedCardIds: t.readonlyArray(RCardIdRef),
    isInMultiSelectMode: t.boolean,
  }),
  t.partial({
    paginationData: RTableOffsetPaginationData,
  }),
]);

export const RContactsManagerListsTableStateDirty = t.intersection([
  RContactsManagerTableStateBase,
  t.type({
    type: t.literal(ContactsManagerTableTypesEnum.LISTS),
    cardGroupIds: t.readonlyArray(RCardGroupIdRef),
    status: RContactsManagerTableStatusesEnum,
    selectedCardGroupIds: t.readonlyArray(RCardGroupIdRef),
    isInMultiSelectMode: t.boolean,
  }),
  t.partial({
    paginationData: RTableTokenPaginationData,
  }),
]);

export const RContactsManagerSearchCardGroupsTableStateDirty = t.intersection([
  RContactsManagerTableStateBase,
  t.type({
    type: t.literal(ContactsManagerTableTypesEnum.SEARCH_CARD_GROUPS),
    cardGroupIds: t.readonlyArray(RCardGroupIdRef),
    status: RContactsManagerTableStatusesEnum,
    selectedCardGroupIds: t.readonlyArray(RCardGroupIdRef),
    isInMultiSelectMode: t.boolean,
  }),
  t.partial({
    paginationData: RTableTokenPaginationData,
  }),
]);

/*
 * Types.
 */

export interface ContactsManagerAccountsTableState
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountsTableStateDirty>> {}
export const RContactsManagerAccountsTableState = t.clean<ContactsManagerAccountsTableState>(
  RContactsManagerAccountsTableStateDirty as any,
);

export interface ContactsManagerAccountContactsTableState
  extends Readonly<t.TypeOf<typeof RContactsManagerAccountContactsTableStateDirty>> {}
export const RContactsManagerAccountContactsTableState = t.clean<ContactsManagerAccountContactsTableState>(
  RContactsManagerAccountContactsTableStateDirty as any,
);

export interface ContactsManagerContactsTableState
  extends Readonly<t.TypeOf<typeof RContactsManagerContactsTableStateDirty>> {}
export const RContactsManagerContactsTableState = t.clean<ContactsManagerContactsTableState>(
  RContactsManagerContactsTableStateDirty as any,
);

export interface ContactsManagerListsTableState
  extends Readonly<t.TypeOf<typeof RContactsManagerListsTableStateDirty>> {}
export const RContactsManagerListsTableState = t.clean<ContactsManagerListsTableState>(
  RContactsManagerListsTableStateDirty as any,
);

export interface ContactsManagerSearchCardGroupsTableState
  extends Readonly<t.TypeOf<typeof RContactsManagerSearchCardGroupsTableStateDirty>> {}
export const RContactsManagerSearchCardGroupsTableState = t.clean<ContactsManagerSearchCardGroupsTableState>(
  RContactsManagerSearchCardGroupsTableStateDirty as any,
);

/*
 * State Types.
 */

const RContactsManagerTableStateDirty = t.taggedUnion('type', [
  RContactsManagerAccountsTableStateDirty,
  RContactsManagerAccountContactsTableStateDirty,
  RContactsManagerContactsTableStateDirty,
  RContactsManagerListsTableStateDirty,
  RContactsManagerSearchCardGroupsTableStateDirty,
]);

export type ContactsManagerTableState =
  | ContactsManagerAccountContactsTableState
  | ContactsManagerAccountsTableState
  | ContactsManagerContactsTableState
  | ContactsManagerListsTableState
  | ContactsManagerSearchCardGroupsTableState;

export const RContactsManagerTableState = t.clean<ContactsManagerTableState>(
  RContactsManagerTableStateDirty as any,
);

/*
 * Type guards.
 */

export function tableStateIsAccountsTableState(
  tableState: ContactsManagerTableState | undefined,
): tableState is ContactsManagerAccountsTableState {
  return tableState?.type === ContactsManagerTableTypesEnum.ACCOUNTS;
}

export function tableStateIsAccountContactsTableState(
  tableState: ContactsManagerTableState | undefined,
): tableState is ContactsManagerAccountContactsTableState {
  return tableState?.type === ContactsManagerTableTypesEnum.ACCOUNT_CONTACTS;
}

export function tableStateIsContactsTableState(
  tableState: ContactsManagerTableState | undefined,
): tableState is ContactsManagerContactsTableState {
  return tableState?.type === ContactsManagerTableTypesEnum.CONTACTS;
}

export function tableStateIsListsTableState(
  tableState: ContactsManagerTableState | undefined,
): tableState is ContactsManagerListsTableState {
  return tableState?.type === ContactsManagerTableTypesEnum.LISTS;
}

export function tableStateIsSearchCardGroupsTableState(
  tableState: ContactsManagerTableState | undefined,
): tableState is ContactsManagerSearchCardGroupsTableState {
  return tableState?.type === ContactsManagerTableTypesEnum.SEARCH_CARD_GROUPS;
}
